import { Drawer } from '@base-ui/react/drawer';
import { XIcon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Button from './Button';

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in the header to the left of the close button. */
  title?: React.ReactNode;
  /** Optional richer header content. Wins over ``title`` when supplied. */
  header?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned bottom bar (typically Cancel + Save). */
  footer?: React.ReactNode;
  /** Max width in px. Default ``SIDE_PANEL_DEFAULT_WIDTH``. */
  maxWidth?: number;
  /** Extra classes on the scrollable body. */
  contentClassName?: string;
  hideCloseButton?: boolean;
  disableBackdropClick?: boolean;
  /**
   * Dock beside the page instead of overlaying it: no backdrop, no focus trap or
   * scroll lock, and the page stays interactive. The caller is responsible for
   * making room (e.g. right padding equal to the panel width) so nothing is hidden.
   */
  docked?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const SIDE_PANEL_DEFAULT_WIDTH = 480;

const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  title,
  header,
  children,
  footer,
  maxWidth = SIDE_PANEL_DEFAULT_WIDTH,
  contentClassName,
  hideCloseButton = false,
  disableBackdropClick = false,
  docked = false,
  className = '',
  'aria-label': ariaLabel
}) => {
  const dialogLabel = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  return (
    <Drawer.Provider>
      <Drawer.Root
        open={open}
        swipeDirection="right"
        modal={!docked}
        disablePointerDismissal={disableBackdropClick || docked}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      >
        <Drawer.Portal>
          {!docked && <Drawer.Backdrop className="fixed inset-0 z-overlay-high bg-black/30" />}
          <Drawer.Viewport
            className={twMerge('fixed inset-0 z-overlay-high flex justify-end', docked && 'pointer-events-none')}
          >
            <Drawer.Popup
              aria-label={dialogLabel}
              style={{ maxWidth }}
              className={twMerge(
                'relative h-dvh w-full bg-white shadow-2xl flex flex-col outline-none',
                docked && 'pointer-events-auto border-l border-border-subtle shadow-lg',
                className
              )}
            >
              {(header || title || !hideCloseButton) && (
                <header className="flex items-start justify-between gap-3 border-b border-border-subtle px-5 pt-5 pb-3 flex-shrink-0">
                  <div className="min-w-0 flex-1">
                    {header ?? (typeof title === 'string' ? <Drawer.Title>{title}</Drawer.Title> : title)}
                  </div>
                  {!hideCloseButton && (
                    <Drawer.Close render={<Button icon={XIcon} variant="ghost" size="sm" aria-label="Close" />} />
                  )}
                </header>
              )}
              <Drawer.Content className={twMerge('flex-1 overflow-y-auto px-5 py-4', contentClassName)}>
                {children}
              </Drawer.Content>
              {footer && (
                <footer className="flex justify-end gap-2 border-t border-border-subtle px-5 py-3 flex-shrink-0 w-full">
                  {footer}
                </footer>
              )}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    </Drawer.Provider>
  );
};

export default SidePanel;
